// Does this lift need a Full Condition Report before ELE quotes or services it? (Session AMF)
//
// THE RULE, in ELE's own published words (server/src/pdf/templates/scheduleOfFees.ts): a Full
// Condition Report is "required where there is no documented evidence of routine maintenance in
// the previous 12 months", before any quote or service proceeds. Until now that rule lived
// in the office manager's head: Session ALW made raising one a real platform action, but nothing
// anywhere NOTICED when one was due. This is the noticing half.
//
// IT IS A PROMPT, NEVER AN ACTION. Nothing here raises anything. It answers one question -- "on
// what this platform holds, is there documented routine maintenance for this lift?" -- and the
// office decides. That distinction is load-bearing, because of the honest limit below.
//
// THE HONEST LIMIT, stated here rather than discovered later: the platform holds ELE's OWN
// records. A lift maintained by somebody else, competently and fully documented in a folder ELE
// has never seen, looks identical here to a lift nobody has touched in five years. That is
// exactly why this asks rather than acts, and exactly why `lastServicedAt` matters so much: it is
// the one field the office can type a previous provider's real date into (the Lifts register's
// "Last serviced" control), which is how third-party evidence gets recorded at all.
//
// WHAT COUNTS AS "DOCUMENTED ROUTINE MAINTENANCE", and what deliberately does not:
//
//   COUNTS  Asset.lastServicedAt -- the authoritative recorded service fact. Stamped
//           monotonically when a SERVICE job is approved, and editable by the office to enter a
//           historical or migrated date. prisma/schema.prisma calls it "a PURE recorded service
//           fact"; that is precisely the shape this question needs.
//   COUNTS  A SERVICE visit on the lift that is complete or submitted for approval. Same fact,
//           read from the other end, so a visit approved minutes ago counts before any anchor
//           re-derivation has run.
//   DOES NOT COUNT  A LOLER thorough examination. This is not a judgement call: it is a separate
//           STATUTORY examination by an independent competent person, not a record of routine
//           maintenance, and ELE's own Schedule of Fees says in terms that a LOLER report alone
//           does not remove the requirement. Counting one would quietly delete the rule for every
//           lift with a current certificate, which is most of them.
//   DOES NOT COUNT  A call-out, a repair or a site inspection. Attending a fault is not routine
//           maintenance, and a look is not maintenance at all.
//
// WHY THE WINDOW IS 12 MONTHS AND NOT 6. The Schedule of Fees used to say "6 to 12 months", a
// range. The Director ruled on 19 October 2026 that it is 12 months: the 6 belongs to the
// six-monthly LOLER interval, and 12 months is the longest a piece of lifting equipment goes
// without service or a thorough examination. The Schedule, the customer pages and the email
// engine's FACTS now all say 12 months, the same figure as this window. Both ends are exported so a Director ruling moves the trigger in one place.
//
// PURE AND DEPENDENCY-FREE, so the two surfaces that ask it (the lift record and the LOLER report
// that is about to become a maintenance quote) share one definition and cannot drift, and so
// server/src/modules/scheduling/conditionReportFeeParity.test.ts can bind the fee below to the
// engine's own FULL_CONDITION_REPORT without dragging any browser code into the server program.

// The locked Schedule of Fees charge for a Full Condition Report, in POUNDS ex VAT. The
// authoritative source is FULL_CONDITION_REPORT in server/src/modules/pricing/fees.ts, which is
// what the published fees PDF renders and what the DRAFT invoice is raised at. This is the client
// mirror (the SPA is a separate bundle and cannot import server code at runtime), bound to it by
// a parity test in exactly the pattern durationForFloors already uses. Never re-type the figure
// at a call site.
export const CONDITION_REPORT_FEE_GBP = 510;

// The published band, both ends. MONTHS is what the trigger actually applies; MONTHS_INNER is
// recorded so the other end of the Director's own wording is not lost, and so a ruling that the
// prompt should fire sooner is a one-line change here rather than a re-derivation.
export const MAINTENANCE_EVIDENCE_MONTHS = 12;
export const MAINTENANCE_EVIDENCE_MONTHS_INNER = 6;

// A job as any of this platform's history feeds report one (ServiceHistoryItem's shape, narrowed
// to the three fields the rule reads). Taking the narrow shape rather than the DTO keeps this
// module free of api.ts and lets a test state a case in one line.
export interface ConditionReportTriggerJob {
  type: string;
  status: string;
  dateIso: string | null;
}

export interface ConditionReportTriggerInput {
  // "lift" | "equipment" | ... . A condition report is a LIFT survey: its checklist is lift areas
  // and its hydraulic-or-traction branch is a lift's drive. Care equipment has its own regime.
  assetClass?: string | null;
  // Whether this lift is currently covered by an active ELE contract. The single clearest "do not
  // ask this" signal: a lift ELE maintains has documented routine maintenance by definition, and
  // this is what keeps the prompt off every ordinary existing customer.
  hasContract?: boolean | null;
  retiredAt?: string | null;
  lastServicedAt?: string | null;
  // Every job this surface can see on the lift. May be a truncated window; see `jobsComplete`.
  jobs?: readonly ConditionReportTriggerJob[];
  // Whether `jobs` is the lift's WHOLE history or only the most recent page of it. It changes one
  // thing and only one: with a truncated window, "no condition report visible" is not proof there
  // is none, so the suppression below is best-effort. Pressing Raise anyway is safe -- the route
  // REUSES an existing open condition report rather than raising a second GBP 510 charge
  // (scheduling.routes.ts, the Job_open_nonservice_per_asset_type recovery) -- so the worst case
  // is one redundant prompt, never a duplicate charge.
  jobsComplete?: boolean;
  now?: Date;
}

export type ConditionReportTriggerReason =
  | "not_a_lift"
  | "retired"
  | "on_contract"
  | "condition_report_on_file"
  | "recent_maintenance"
  | "no_maintenance_evidence";

export interface ConditionReportTrigger {
  required: boolean;
  reason: ConditionReportTriggerReason;
  // The most recent documented routine maintenance the platform holds, ISO, or null when it holds
  // none. Populated even when `required` is false, so a surface can say WHEN rather than only that
  // the answer is no.
  lastEvidence: string | null;
}

function parseIso(v: string | null | undefined): number | null {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
}

// The statuses at which a SERVICE visit is documented work rather than a plan. `complete` is the
// approved record; `pending_approval` carries a submitted engineer sheet, which is a record of a
// visit that physically happened even though the office has not signed it off. A `scheduled` or
// `in_progress` visit is neither, and counting one would let a booking that has not happened yet
// answer a question about the past.
const DOCUMENTED_SERVICE_STATUSES = new Set(["complete", "pending_approval"]);

/**
 * The most recent documented routine maintenance this platform holds for the lift, ISO, or null.
 * Exported for the surfaces that want to state the date beside the prompt.
 */
export function lastDocumentedMaintenance(input: ConditionReportTriggerInput): string | null {
  let bestIso: string | null = null;
  let bestT = Number.NEGATIVE_INFINITY;

  const stamped = parseIso(input.lastServicedAt);
  if (stamped != null) {
    bestIso = input.lastServicedAt ?? null;
    bestT = stamped;
  }
  for (const job of input.jobs ?? []) {
    if (job.type !== "service") continue;
    if (!DOCUMENTED_SERVICE_STATUSES.has(job.status)) continue;
    const t = parseIso(job.dateIso);
    if (t == null || t <= bestT) continue;
    bestT = t;
    bestIso = job.dateIso;
  }
  return bestIso;
}

/**
 * Is a Full Condition Report required for this lift before ELE quotes or services it, on the
 * evidence this platform holds? Pure. Raises nothing and writes nothing.
 */
export function conditionReportTrigger(input: ConditionReportTriggerInput): ConditionReportTrigger {
  const lastEvidence = lastDocumentedMaintenance(input);
  const not = (reason: ConditionReportTriggerReason): ConditionReportTrigger => ({ required: false, reason, lastEvidence });

  // A condition report is a lift survey. `assetClass` absent means the caller did not say, and the
  // register's own default is `lift`, so an unstated class is treated as a lift rather than
  // silently excluding the very records a leaner DTO describes.
  if (input.assetClass != null && input.assetClass !== "lift") return not("not_a_lift");
  if (input.retiredAt) return not("retired");
  // The ordinary existing customer. ELE services this lift, so ELE holds its maintenance record.
  if (input.hasContract === true) return not("on_contract");

  // Already surveyed, or a survey already raised and waiting. Any status: a completed one answers
  // the question, and an open one means the office has already decided and does not need asking
  // twice. Best-effort when the caller's window is truncated (see `jobsComplete`).
  if ((input.jobs ?? []).some((j) => j.type === "condition_report")) return not("condition_report_on_file");

  const now = (input.now ?? new Date()).getTime();
  const evidenceT = parseIso(lastEvidence);
  if (evidenceT != null) {
    const cutoff = new Date(now);
    cutoff.setMonth(cutoff.getMonth() - MAINTENANCE_EVIDENCE_MONTHS);
    // A FUTURE-dated evidence date is still evidence and is not treated as absent: the register
    // already refuses a future lastServicedAt (asset.routes.ts FUTURE_LAST_SERVICED_MESSAGE), so
    // one can only arrive from data this rule has no business second-guessing.
    if (evidenceT >= cutoff.getTime()) return not("recent_maintenance");
  }

  return { required: true, reason: "no_maintenance_evidence", lastEvidence };
}
